import { cn } from '@/utils/tailwind';

/**
 * JavaScript implementation to estimate P90 using log-normal distribution
 * @param p50 - 50th percentile latency value
 * @param p99 - 99th percentile latency value
 * @returns Estimated 90th percentile latency value
 */
export function estimateP90LogNormal(p50: number, p99: number): number {
  const logP50 = Math.log(p50);
  const logP99 = Math.log(p99);
  const sigma = (logP99 - logP50) / 2.326; // 99th percentile z-score
  const logP90 = logP50 + 1.282 * sigma; // 90th percentile z-score
  return Math.exp(logP90);
}

/**
 * Get latency theme classes based on fixed thresholds
 * @param latency - Latency value (number in seconds or string with 's' suffix)
 * @returns CSS class string for latency theme or undefined if invalid latency
 */
export function getLatencyTheme(latency: string | number): string | undefined {
  const latencySec =
    typeof latency === 'number' && !Number.isNaN(latency)
      ? latency
      : typeof latency === 'string' && latency.endsWith('s')
      ? parseFloat(latency.replace('s', ''))
      : null;

  if (latencySec === null || Number.isNaN(latencySec)) return undefined;
  if (latencySec > 7) {
    return cn('bg-error-primary text-error');
  }
  if (latencySec > 3) {
    return cn('bg-warning-primary text-warning');
  }

  return cn('bg-success-primary text-success');
}

/**
 * Get latency theme classes based on percentile thresholds
 * @param latency - Latency value (number in seconds or string with 's' suffix)
 * @param latencyP50 - 50th percentile latency value for the dataset
 * @param latencyP99 - 99th percentile latency value for the dataset
 * @returns CSS class string for latency theme or undefined if invalid latency
 */
export function getLatencyThemePercentile(
  latency: string | number,
  latencyP50?: number,
  latencyP99?: number
): string | undefined {
  const latencySec =
    typeof latency === 'number' && !Number.isNaN(latency)
      ? latency
      : typeof latency === 'string' && latency.endsWith('s')
      ? parseFloat(latency.replace('s', ''))
      : null;

  if (latencySec === null || Number.isNaN(latencySec)) return undefined;

  // If we don't have percentile data, fall back to fixed thresholds
  if (!latencyP50 || !latencyP99) {
    return getLatencyTheme(latency);
  }

  const p90 = estimateP90LogNormal(latencyP50, latencyP99);

  // p90+ values are red, p50-90 yellow and below p50 green
  if (latencySec >= p90) {
    return cn('bg-error-primary text-error');
  }

  if (latencySec >= latencyP50) {
    return cn('bg-warning-primary text-warning');
  }

  return cn('bg-success-primary text-success');
}
